/**
 * Dissociation, synthesis, and power — spec §4, §5, §8 steps 2-5.
 *
 * REBUILT after the Phase 8 harness showed the original model could not produce
 * a working economy. Two principles, and the old version violated both:
 *
 *   1. EVERY STAGE IS RATE-LIMITED. Spec §13 item 6 left refining "purely
 *      energy-limited"; measurement showed that fails. §5 gives every consumer
 *      one shared proportional throttle, so an uncapped dissociation intent bids
 *      in proportion to the SIZE OF THE STOCKPILE rather than the work being
 *      done. Mining more collapsed the ratio and starved synthesis: millions of
 *      units mined, zero goods, zero buildings. A cap makes demand track
 *      throughput instead of backlog.
 *
 *   2. CONTESTED INPUTS ARE SHARED, NOT CLAIMED. The old synthesis let each
 *      recipe consume as much of an element as it could before the next one ran,
 *      so any two recipes needing the same element were mutually exclusive and
 *      the loser produced exactly zero forever. Concrete — in five of six
 *      buildings — was never made at all. Now recipes take turns.
 *
 * The plan/apply split from the original is kept, because it was right: steps 2,
 * 3 and 4 only PLAN and mutate nothing; step 5 computes one throttle ratio and
 * is the sole place inventory changes. That is what keeps spec §8's "compute
 * intended demand, then the ratio, then apply it uniformly" true by construction
 * rather than by remembering to do it.
 */

import { apportion } from './apportion';
import {
  ATMOSPHERIC_ENERGY_PER_UNIT,
  ATMOSPHERIC_N_RESERVE,
  ATMOSPHERIC_YIELD_N,
  BASE_ATMOSPHERIC_INTAKE_PER_TICK,
  DISSOCIATION_ENERGY_PER_UNIT,
  DISSOCIATION_THROUGHPUT_BASE,
  MANUFACTURING_PER_DISSOCIATION_UNIT,
  MANUFACTURING_PER_SYNTHESIS_UNIT,
  SYNTHESIS_THROUGHPUT_BASE,
} from './constants';
import {
  BUILDINGS,
  ELEMENT_IDS,
  GOODS,
  GOOD_IDS,
  MINERALS,
  MINERAL_IDS,
  type ElementId,
  type GoodId,
  type MineralId,
} from './data';
import type { GameState } from './types';

const BP = 10000;

/** What steps 2-4 intend to do, before power is taken into account. */
export interface FactoryPlan {
  /** Atmospheric units drawn. spec §8 step 2 */
  intakeUnits: number;
  /** Mineral units dissociated, per mineral. spec §8 step 3 */
  dissociate: Partial<Record<MineralId, number>>;
  /** Goods synthesised, per good. spec §8 step 4 */
  synthesize: Partial<Record<GoodId, number>>;
  /** Standing draw of completed buildings. Not a rate — it just is. */
  upkeep: number;
  /** Total intended draw, including upkeep. */
  demand: number;
  /** What synthesis ACTUALLY produced after throttling. Filled by step 5. */
  produced: Partial<Record<GoodId, number>>;
}

export function emptyPlan(): FactoryPlan {
  return {
    intakeUnits: 0,
    dissociate: {},
    synthesize: {},
    upkeep: 0,
    demand: 0,
    produced: {},
  };
}

// --- Factory capability ------------------------------------------------------

/**
 * Total manufacturing output of completed buildings.
 *
 * Spec §10 defines factory districts as producing "250 manufacturing" and gives
 * it no consumer — outputs were specified early so the deferred colonist layer
 * would not force a data-model change. This is its consumer: manufacturing is
 * the factory's upgrade path, raising both throughput ceilings. It is what §13
 * item 6 means by "a hard cap would make factory upgrades meaningful".
 */
export function manufacturingOutput(state: GameState): number {
  let total = 0;
  for (const row of state.city.grid) {
    for (const plot of row) {
      if (plot === null || plot.state !== 'complete' || !plot.enabled) continue;
      const building = BUILDINGS[plot.buildingType];
      if (building.output.kind === 'manufacturing') total += building.output.amount;
    }
  }
  return total;
}

/** Mineral units dissociable this tick. */
export function dissociationThroughput(state: GameState): number {
  return (
    DISSOCIATION_THROUGHPUT_BASE +
    Math.floor(manufacturingOutput(state) / MANUFACTURING_PER_DISSOCIATION_UNIT)
  );
}

/** Goods synthesisable this tick, across all recipes together. */
export function synthesisThroughput(state: GameState): number {
  return (
    SYNTHESIS_THROUGHPUT_BASE +
    Math.floor(manufacturingOutput(state) / MANUFACTURING_PER_SYNTHESIS_UNIT)
  );
}

/**
 * The factory's solar collectors, plus every running power plant. spec §5, §10
 *
 * Solar is the floor the colony starts on and never loses. It is deliberately
 * modest — enough to run the refinery and light a handful of buildings — so that
 * the first power plant is a milestone rather than a formality.
 */
export function powerCapacity(state: GameState): number {
  let capacity = state.factory.solarOutput;
  for (const row of state.city.grid) {
    for (const plot of row) {
      if (plot === null || plot.state !== 'complete' || !plot.enabled) continue;
      const building = BUILDINGS[plot.buildingType];
      if (building.output.kind === 'energy') capacity += building.output.amount;
    }
  }
  return capacity;
}

/** Standing upkeep of completed buildings. spec §10 */
export function buildingUpkeep(state: GameState): number {
  let upkeep = 0;
  for (const row of state.city.grid) {
    for (const plot of row) {
      if (plot === null || plot.state !== 'complete' || !plot.enabled) continue;
      upkeep += BUILDINGS[plot.buildingType].upkeep;
    }
  }
  return upkeep;
}

// --- Step 2: atmospheric intake ----------------------------------------------

/**
 * spec §8 step 2, §4.2.
 *
 * Runs only while fertilizer synthesis is enabled. §4.2 describes drawing "up to
 * ratePerTick" but nothing switches the intake off, and an intake that always
 * runs burns 40% of base power accumulating nitrogen only one recipe consumes.
 * Tying it to that consumer keeps it the strategic choice §4.2 describes rather
 * than a standing tax. See SPEC-ERRATA.md E10.
 */
export function planIntake(state: GameState, plan: FactoryPlan): void {
  const setting = state.factory.synthesis.fertilizer;
  if (!setting.enabled) return;

  // Nothing waiting on it. Both tests are about not paying 80 energy a tick for
  // nitrogen the colony has no use for: the first when the player has capped
  // fertilizer, the second when the pile is already deeper than synthesis can
  // work through. See ATMOSPHERIC_N_RESERVE.
  if (setting.target > 0 && state.inventory.goods.fertilizer >= setting.target) return;
  if (state.inventory.elements.N >= ATMOSPHERIC_N_RESERVE) return;

  const rate = Math.floor(
    BASE_ATMOSPHERIC_INTAKE_PER_TICK * state.planet.atmosphere.density,
  );
  if (rate <= 0) return;

  plan.intakeUnits = rate;
  plan.demand += rate * ATMOSPHERIC_ENERGY_PER_UNIT;
}

// --- Step 3: dissociation ----------------------------------------------------

/**
 * spec §8 step 3, §4.1.
 *
 * The stockpile is drawn down in PROPORTION to what is held, up to this tick's
 * throughput. Proportional rather than in mineral order: the factory feeds its
 * hoppers from the pile it has, and an order-based draw silently refines only
 * whichever mineral sorts first — which is exactly the bug the first attempt at
 * this cap shipped, producing nothing but iron and oxygen from a mixed pile.
 */
export function planDissociation(state: GameState, plan: FactoryPlan): void {
  const throughput = dissociationThroughput(state);
  if (throughput <= 0) return;

  const share = apportion(
    MINERAL_IDS,
    (id) => state.inventory.minerals[id],
    throughput,
    (id) => state.inventory.minerals[id],
  );

  for (const id of MINERAL_IDS) {
    const units = share[id];
    if (units <= 0) continue;
    plan.dissociate[id] = units;
    plan.demand += units * DISSOCIATION_ENERGY_PER_UNIT;
  }
}

// --- Step 4: synthesis -------------------------------------------------------

function canMakeOne(
  good: GoodId,
  elements: Record<ElementId, number>,
  goods: Record<GoodId, number>,
): boolean {
  const recipe = GOODS[good];
  for (const id of ELEMENT_IDS) {
    const per = recipe.recipe[id];
    if (per !== undefined && elements[id] < per) return false;
  }
  if (recipe.ingredients !== undefined) {
    for (const id of GOOD_IDS) {
      const per = recipe.ingredients[id];
      if (per !== undefined && goods[id] < per) return false;
    }
  }
  return true;
}

function consumeOne(
  good: GoodId,
  elements: Record<ElementId, number>,
  goods: Record<GoodId, number>,
): void {
  const recipe = GOODS[good];
  for (const id of ELEMENT_IDS) {
    const per = recipe.recipe[id];
    if (per !== undefined) elements[id] -= per;
  }
  if (recipe.ingredients !== undefined) {
    for (const id of GOOD_IDS) {
      const per = recipe.ingredients[id];
      if (per !== undefined) goods[id] -= per;
    }
  }
  goods[good] += 1;
}

/** Enabled recipes, lowest priority number first. Ties by spec §4.4 table order. */
/**
 * Which recipes want to run, in the order they get their turn.
 *
 * A recipe is skipped when it is switched off, and when the colony already
 * holds its target. Target zero means no target — see SynthesisSetting.
 */
function synthesisOrder(state: GameState): GoodId[] {
  return [...GOOD_IDS]
    .filter((id) => {
      const setting = state.factory.synthesis[id];
      if (!setting.enabled) return false;
      return setting.target <= 0 || state.inventory.goods[id] < setting.target;
    })
    .sort((a, b) => {
      const byPriority =
        state.factory.synthesis[a].priority - state.factory.synthesis[b].priority;
      // GOOD_IDS index breaks ties, so ordering never depends on sort stability.
      return byPriority !== 0 ? byPriority : GOOD_IDS.indexOf(a) - GOOD_IDS.indexOf(b);
    });
}

/**
 * spec §8 step 4, §4.4.
 *
 * Recipes take turns. Each round, every enabled recipe in priority order makes
 * ONE unit if it can, until this tick's throughput is spent or nothing more can
 * be made. Priority still decides who goes first, and so who wins the last
 * partial round when inputs are tight — but it no longer lets one recipe drain
 * an element the others also need.
 *
 * Round-robin rather than a proportional split because the recipes have
 * different formulas over overlapping inputs; taking turns keeps the arithmetic
 * exactly integral and needs no reconciliation pass. Cost is bounded by
 * throughput times recipe count — a few hundred steps per tick.
 *
 * It also handles the one good made from another: fertilizer produced in an
 * early round is available to topsoil in a later one, inside the same tick.
 */
export function planSynthesis(state: GameState, plan: FactoryPlan): void {
  const order = synthesisOrder(state);
  if (order.length === 0) return;

  // Scratch ledger, so planning never touches real inventory.
  const elements = { ...state.inventory.elements };
  const goods = { ...state.inventory.goods };

  let budget = synthesisThroughput(state);

  while (budget > 0) {
    let madeSomething = false;

    for (const id of order) {
      if (budget <= 0) break;
      // Re-checked against the SCRATCH ledger, not the real one: the order was
      // filtered once before the loop, and without this a single tick's budget
      // could carry a good far past the target the player set.
      const target = state.factory.synthesis[id].target;
      if (target > 0 && goods[id] >= target) continue;
      if (!canMakeOne(id, elements, goods)) continue;

      consumeOne(id, elements, goods);
      plan.synthesize[id] = (plan.synthesize[id] ?? 0) + 1;
      plan.demand += GOODS[id].energy;
      budget -= 1;
      madeSomething = true;
    }

    // A full round in which nothing could be made will not improve on the next
    // one either — the ledger only shrinks.
    if (!madeSomething) break;
  }
}

// --- Step 5: resolve power and apply -----------------------------------------

/**
 * spec §8 step 5, §5.
 *
 * One pass, no iteration. Every consumer runs at exactly capacity/demand, so
 * total consumption lands on capacity rather than overshooting it.
 */
export function resolvePowerAndApply(state: GameState, plan: FactoryPlan): void {
  const capacity = powerCapacity(state);
  plan.upkeep = buildingUpkeep(state);
  plan.demand += plan.upkeep;

  const throttleBp =
    plan.demand <= capacity || plan.demand === 0
      ? BP
      : Math.floor((capacity * BP) / plan.demand);

  state.power = { capacity, demand: plan.demand, throttleBp };

  const scale = (intended: number): number => Math.floor((intended * throttleBp) / BP);

  /**
   * Throttle a set of per-item intents down to a total, sharing the cut.
   *
   * Flooring each item independently is wrong and not harmlessly so: a recipe
   * planned for one unit produces ZERO at any throttle below 100%, so the
   * smallest producer is deleted rather than slowed. Soil — one unit per tick,
   * last in the round-robin — vanished entirely for that reason, and farms need
   * it. Apportioning the throttled total instead means small claims still get
   * their turn, and the total still lands exactly where the ratio says.
   */
  const throttleShare = <K extends string>(
    keys: readonly K[],
    intents: Partial<Record<K, number>>,
  ): Record<K, number> => {
    let intended = 0;
    for (const key of keys) intended += intents[key] ?? 0;
    return apportion(
      keys,
      (key) => intents[key] ?? 0,
      scale(intended),
      (key) => intents[key] ?? 0,
    );
  };

  // --- intake ---
  const intake = scale(plan.intakeUnits);
  if (intake > 0) state.inventory.elements.N += intake * ATMOSPHERIC_YIELD_N;

  // --- dissociation ---
  const refined = throttleShare(MINERAL_IDS, plan.dissociate);
  for (const id of MINERAL_IDS) {
    const units = Math.min(refined[id], state.inventory.minerals[id]);
    if (units <= 0) continue;

    state.inventory.minerals[id] -= units;
    const yields = MINERALS[id].yields;
    for (const elementId of ELEMENT_IDS) {
      const per = yields[elementId];
      if (per === undefined) continue;
      state.inventory.elements[elementId] += per * units;
    }
  }

  // --- synthesis ---
  // Re-clamped against real inventory rather than trusted from the plan: the
  // plan was built before dissociation ran, so stocks have moved, and a
  // throttled-down run must never overdraw an element another recipe consumed.
  const allowed = throttleShare(GOOD_IDS, plan.synthesize);
  for (const id of synthesisOrder(state)) {
    const good = GOODS[id];
    let units = allowed[id];
    if (units <= 0) continue;

    for (const elementId of ELEMENT_IDS) {
      const per = good.recipe[elementId];
      if (per === undefined || per === 0) continue;
      units = Math.min(units, Math.floor(state.inventory.elements[elementId] / per));
    }
    if (good.ingredients !== undefined) {
      for (const ingredientId of GOOD_IDS) {
        const per = good.ingredients[ingredientId];
        if (per === undefined || per === 0) continue;
        units = Math.min(units, Math.floor(state.inventory.goods[ingredientId] / per));
      }
    }
    if (units <= 0) continue;

    for (const elementId of ELEMENT_IDS) {
      const per = good.recipe[elementId];
      if (per === undefined) continue;
      state.inventory.elements[elementId] -= per * units;
    }
    if (good.ingredients !== undefined) {
      for (const ingredientId of GOOD_IDS) {
        const per = good.ingredients[ingredientId];
        if (per === undefined) continue;
        state.inventory.goods[ingredientId] -= per * units;
      }
    }
    state.inventory.goods[id] += units;
    plan.produced[id] = (plan.produced[id] ?? 0) + units;
  }
}
